import { Plus, X } from 'lucide-react';
import { getBpmnEventIcon, InfoHint, STEP_FIELD_ICONS, type BpmnEventType } from '@bliks-io/ui';
import { useTranslation } from 'react-i18next';
import { type ProcessStepRecording } from '@bliks-io/contracts';

interface InputsSectionProps {
  recording: ProcessStepRecording | null;
  onAddInput: () => void;
  onEditInput: (index: number) => void;
  onRemoveInput: (index: number) => void;
}

/**
 * Unified inputs section for the step card node (I Receive)
 * Displays both event and previous_output inputs in a single list
 */
export function InputsSection({
  recording,
  onAddInput,
  onEditInput,
  onRemoveInput
}: InputsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-bg-1 border border-border-14 rounded-lg p-2">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <STEP_FIELD_ICONS.input className="h-3.5 w-3.5 text-fg-secondary" />
          <span className="text-sm font-semibold text-foreground">
            {t('viralRecording.sections.inputsReceived') || 'Events & Information'}
          </span>
          <InfoHint content={t('viralRecording.tooltips.input')} />
        </div>
        <button
          onClick={onAddInput}
          className="flex items-center gap-1.5 text-sm text-fg-secondary hover:text-foreground transition-colors"
        >
          <span>{t('viralRecording.buttons.addNewInput') || 'Add Input'}</span>
          <div className="flex items-center justify-center w-5 h-5 bg-white border border-line-strong rounded-full">
            <Plus size={13} className="text-fg-secondary" />
          </div>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {recording?.inputs?.map((input, index) => {
          // Render based on input type
          if (input.inputType === 'event') {
            const EventIcon = getBpmnEventIcon((input.type || 'message') as BpmnEventType);
            return (
              <div
                key={index}
                className="relative bg-white border border-line-strong rounded-md min-w-[140px] group"
              >
                <div className="px-3 py-2 cursor-pointer" onClick={() => onEditInput(index)}>
                  <div className="flex items-center gap-1.5 text-2xs font-medium text-muted-foreground mb-1">
                    <EventIcon size={12} />
                    <span>
                      {t('viralRecording.labels.event')} {index + 1}
                    </span>
                  </div>
                  <div className="pr-4 text-sm font-medium text-foreground">
                    {input.name || t('viralRecording.labels.untitled')}
                  </div>
                </div>
                {!input.notRemovable && (
                  <button
                    className="absolute top-1 right-1 p-0.5 text-fg-subtle hover:text-fg-secondary hover:bg-muted rounded-sm transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveInput(index);
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          } else {
            // previous_output type
            return (
              <div
                key={index}
                className="relative bg-white border border-line-strong rounded-md min-w-[140px] group"
              >
                <div className="px-3 py-2 cursor-pointer" onClick={() => onEditInput(index)}>
                  <div className="text-2xs font-medium text-muted-foreground mb-1">
                    {t('viralRecording.labels.information')} {index + 1}
                  </div>
                  <div className="pr-4 text-sm font-medium text-foreground">
                    {input.name || t('viralRecording.labels.untitled')}
                  </div>
                </div>
                {!input.notRemovable && (
                  <button
                    className="absolute top-1 right-1 p-0.5 text-fg-subtle hover:text-fg-secondary hover:bg-muted rounded-sm transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveInput(index);
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          }
        })}
        {(!recording?.inputs || recording.inputs.length === 0) && (
          <div className="text-sm italic text-fg-subtle">
            {t('viralRecording.placeholders.noInputsAddedYet') || 'No inputs added yet'}
          </div>
        )}
      </div>
    </div>
  );
}
